import { Box, Divider, Grid, Skeleton, Stack, Typography } from "@mui/material";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import PersonIcon from "@mui/icons-material/Person";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useEffect, useRef, useState } from "react";
import { DateRange } from "models/interfaces";
import {
  fetchContactsGraph,
  fetchDateWiseContactsCount,
  fetchTotalActiveCount,
} from "../DashboardService";
import {
  calculateDaysBetween,
  getFormattedStatsCount,
  handleErrorMessage,
  last30DaysFromDate,
  last30DaysToDate,
} from "helpers/methods";
import moment from "moment";
import EmailSentOverView from "./EmailSentOverView";
import DashboardDateRangePicker from "./DashboardDateRangePicker";
import CustomInfoTooltip from "global/components/CustomInfoTooltip/CustomInfoTooltip";

const SubscriberOverview = () => {
  const [chartOptions, setChartOptions] = useState<ApexOptions>({
    chart: {
      toolbar: { show: true },
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 0.2,
        opacityFrom: 0.3,
        opacityTo: 0.1,
        stops: [20, 100],
      },
    },
    tooltip: {
      enabled: true,
      theme: "dark",
      y: {
        formatter: (value: any) => value.toFixed(0),
      },
    },
    grid: {
      borderColor: "#e0e0e0",
      strokeDashArray: 4,
    },
    xaxis: {
      categories: [],
      labels: {
        style: {
          fontFamily: "Inter",
          fontSize: "12px",
          fontWeight: 400,
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (value) => value.toFixed(0),
        style: {
          fontFamily: "Inter",
          fontSize: "12px",
          fontWeight: 400,
        },
      },
    },
    colors: ["#7551D0"], // Line color
  });

  const [series, setSeries] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [openContactsDateRangePicker, setOpenContactsDateRangePicker] =
    useState<boolean>(false);
  const [totalActiveContactsCount, setTotalActiveContactsCount] =
    useState<number>(0);
  const [todayContacts, setTodayContacts] = useState<number>(0);
  const [thisMontsContacts, setThisMontsContacts] = useState<number>(0);
  const [customDateRangeActiveContacts, setCustomDateRangeActiveContacts] =
    useState<number>(0);
  const [customDateRangeUnsubContacts, setCustomDateRangeUnsubContacts] =
    useState<number>(0);

  const [dateRange, setDateRange] = useState<DateRange>({
    fromDate: last30DaysFromDate,
    toDate: last30DaysToDate,
  });
  const dateRangeRef = useRef(dateRange);

  useEffect(() => {
    dateRangeRef.current = dateRange;
  }, [dateRange, setDateRange]);

  useEffect(() => {
    getContactDateWiseCount(dateRange);
  }, []);

  const getContactDateWiseCount = async (dateRange: DateRange) => {
    const todayFromDate = moment().format("YYYY-MM-DD");
    const todayToDate = moment().format("YYYY-MM-DD");
    const thisMonthFromDate = moment().startOf("month").format("YYYY-MM-DD");
    const thisMonthToDate = moment().format("YYYY-MM-DD");

    try {
      setLoading(true);
      const [
        totalActiveContactsCount,
        graphData,
        today,
        thisMonth,
        customDateRangeActiveContacts,
        customDateRangeUnsubContacts,
      ] = await Promise.all([
        fetchTotalActiveCount(),
        fetchContactsGraph(dateRange.fromDate, dateRange.toDate, "", 1),
        fetchDateWiseContactsCount(todayFromDate, todayToDate, "", 1),
        fetchDateWiseContactsCount(thisMonthFromDate, thisMonthToDate, "", 1),
        fetchDateWiseContactsCount(dateRange.fromDate, dateRange.toDate, "", 1),
        fetchDateWiseContactsCount(dateRange.fromDate, dateRange.toDate, "", 0),
      ]);
      const allDates = Object.keys(graphData).map((date) =>
        new Date(date).toLocaleDateString("en-US", {
          month: "short",
          day: "2-digit",
        })
      );

      const dataValues = Object.values(graphData);
      setSeries([{ name: "Count", data: dataValues }]);
      setChartOptions((prevOptions) => ({
        ...prevOptions,
        xaxis: {
          ...prevOptions.xaxis,
          categories: allDates,
          tickAmount: Math.ceil(allDates.length / 2),
        },
      }));
      setTotalActiveContactsCount(totalActiveContactsCount);
      setTodayContacts(today);
      setThisMontsContacts(thisMonth);
      setCustomDateRangeActiveContacts(customDateRangeActiveContacts);
      setCustomDateRangeUnsubContacts(customDateRangeUnsubContacts);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    }
  };

  const getAdditionalMetrics = () => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <Stack direction="column">
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body1" fontWeight={500}>
                  New contacts today
                </Typography>
              </Stack>
              <Typography variant="h4">
                {getFormattedStatsCount(todayContacts ?? 0)}
              </Typography>
            </Stack>
            <Divider flexItem sx={{ mt: 1 }} />
          </Stack>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <Stack direction="column">
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body1" fontWeight={500}>
                  New contacts in this month
                </Typography>
              </Stack>
              <Typography variant="h4">
                {getFormattedStatsCount(thisMontsContacts ?? 0)}
              </Typography>
            </Stack>
            <Divider flexItem sx={{ mt: 1 }} />
          </Stack>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <Stack direction="column">
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body1" fontWeight={500}>
                  {calculateDaysBetween(dateRange) === 0
                    ? "New contacts today"
                    : `New in last ${calculateDaysBetween(dateRange)} days`}
                </Typography>
              </Stack>
              <Typography variant="h4">
                {getFormattedStatsCount(customDateRangeActiveContacts ?? 0)}
              </Typography>
            </Stack>
            <Divider flexItem sx={{ mt: 1 }} />
          </Stack>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <Stack direction="column">
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body1" fontWeight={500}>
                  {calculateDaysBetween(dateRange) === 0
                    ? "Unsubscribed today"
                    : `Unsubscribed in last ${calculateDaysBetween(
                        dateRange
                      )} days`}
                </Typography>
              </Stack>
              <Typography variant="h4">
                {getFormattedStatsCount(customDateRangeUnsubContacts ?? 0)}
              </Typography>
            </Stack>
            <Divider flexItem sx={{ mt: 1 }} />
          </Stack>
        </Grid>
      </Grid>
    );
  };
  const getSubscriberCounts = () => {
    return (
      <CenteredCardLayout removeLastChildBottomPadding>
        <Stack
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          p={2}
        >
          <Box display="flex" flexDirection="row" alignItems="center">
            <PersonIcon sx={{ color: "#B9BABB", pr: 1 }} />
            <Typography variant="h4">Contacts</Typography>
          </Box>
          <DashboardDateRangePicker
            openDateRangePicker={openContactsDateRangePicker}
            setOpenDateRangePicker={setOpenContactsDateRangePicker}
            dateRange={dateRange}
            setDateRange={setDateRange}
            helperFunction={getContactDateWiseCount}
          />
        </Stack>
        <Divider />
        <Stack>
          <Stack spacing={1} p={2}>
            <Stack direction="row" alignItems="center" columnGap={1}>
              <Typography>Total active contacts</Typography>{" "}
              <CustomInfoTooltip
                placement="bottom"
                infoMessage={
                  "This count represents the total of your private group contacts and all public group contacts."
                }
              />
            </Stack>

            <Typography variant="h5">
              {getFormattedStatsCount(totalActiveContactsCount ?? 0)}
            </Typography>
          </Stack>
        </Stack>
        <Stack>
          <Box p={2}>{getAdditionalMetrics()}</Box>
          <Box px={2}>
            <ReactApexChart
              options={chartOptions}
              series={series}
              type="area"
              height={300}
            />
          </Box>
        </Stack>
      </CenteredCardLayout>
    );
  };

  const getAdditionalMetricsSkeleton = () => {
    return (
      <Grid container spacing={3}>
        {Array(4)
          .fill(0)
          .map((_, index) => (
            <Grid key={index} item xs={12} sm={12} md={6} lg={6} xl={6}>
              <Stack direction="column">
                <Stack direction="row" justifyContent="space-between">
                  <Skeleton variant="text" width={150} height={24} />
                  <Skeleton variant="text" width={60} height={32} />
                </Stack>
                <Divider flexItem sx={{ mt: 1 }} />
              </Stack>
            </Grid>
          ))}
      </Grid>
    );
  };

  const getSubscriberCountsSkeleton = () => {
    return (
      <CenteredCardLayout removeLastChildBottomPadding>
        <Stack
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
          p={2}
        >
          <Box display="flex" flexDirection="row" alignItems="center">
            <Skeleton variant="text" width={100} height={32} />
          </Box>
          <Skeleton variant="rounded" width={200} height={40} />
        </Stack>
        <Divider />
        <Stack>
          <Box p={2}>
            <Skeleton variant="text" width={150} height={24} />
            <Skeleton variant="text" width={80} height={32} />
          </Box>
        </Stack>
        <Stack>
          <Box p={2}>{getAdditionalMetricsSkeleton()}</Box>
          <Box p={2}>
            <Skeleton variant="rounded" width="100%" height={250} />
          </Box>
        </Stack>
      </CenteredCardLayout>
    );
  };

  const getPerformanceOverViewLoader = () => {
    return (
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h4" color="text.primary">
          <Skeleton width={200} />
        </Typography>
      </Box>
    );
  };

  return (
    <Stack direction="column" spacing={2}>
      <EmailSentOverView />
      {loading ? (
        getPerformanceOverViewLoader()
      ) : (
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h4" color="text.primary">
            Performance Overview
          </Typography>
        </Box>
      )}
      {loading ? getSubscriberCountsSkeleton() : getSubscriberCounts()}
    </Stack>
  );
};

export default SubscriberOverview;
